import { Button, Checkbox, Collapse } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Text } from 'vet-bones/components';

import {
  TEST_IDS,
  getPublishProjectDrawerModelRowTestId,
} from 'src/constants/testIds';
import {
  useModelNamePlaceholderByModelId,
  useModelNamedGraphPlaceholderByModelId,
  useValidateModelName,
  useValidateNamedGraphIri,
} from 'src/containers/PublishProjectDrawer/hooks';
import { ModelMappingForm } from 'src/containers/PublishProjectDrawer/ModelMappingForm';
import {
  CreationType,
  ModelInfo,
  ModelsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { ModelSelectItem } from 'src/selectors/modelItems';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ModelMappingRowProps = {
  databaseId: string;
  existingModel: ModelSelectItem | null;
  modelInfo: ModelInfo;
  modelNamePlaceholder: string;
  namedGraphIriPlaceholder: string;
  handleChangeCreationType: (creationType: CreationType) => void;
  handleSelectExistingModel: (modelItem: ModelSelectItem | null) => void;
  handleSetModelInfo: (nextInfo: Partial<ModelInfo>) => void;
  testId: string;
  validateModelName: (modelName: string) => string;
  validateNamedGraph: (modelName: string) => string;
};

export const ModelMappingRow: React.VFC<ModelMappingRowProps> = ({
  databaseId,
  existingModel,
  handleChangeCreationType,
  handleSelectExistingModel,
  handleSetModelInfo,
  modelInfo,
  modelNamePlaceholder,
  namedGraphIriPlaceholder,
  testId,
  validateModelName,
  validateNamedGraph,
}) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const [isOpen, setIsOpen] = React.useState(() => modelInfo.shouldPublish);

  return (
    <div
      key={modelInfo.projectModelId}
      className="sd-model-row"
      data-testid={testId}
    >
      <div className="sd-model-row__body">
        <div className="sd-model-row-grid">
          <div className="sd-model-row-grid__checkbox">
            <Checkbox
              checked={modelInfo.shouldPublish}
              data-testid={TEST_IDS.publishProjectDrawer.model.checkbox}
              onChange={() => {
                handleSetModelInfo({ shouldPublish: !modelInfo.shouldPublish });
                setIsOpen(!modelInfo.shouldPublish);
              }}
            />
          </div>
          <div className="sd-model-row-grid__model">
            <Text className="sd-model-row-grid__model-name" ellipsize>
              {project?.modelsById[modelInfo.projectModelId].name ||
                // we dont want to use modelInfo.modelName since it changes,
                // but it shouldn't reach this part of the code
                modelInfo.modelName}
            </Text>
            <div className="sd-model-row-grid__model-collapse-button">
              {!modelInfo.shouldPublish ? null : (
                <Button
                  data-testid={
                    TEST_IDS.publishProjectDrawer.model.collapseButton
                  }
                  icon={isOpen ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN}
                  minimal
                  onClick={() => setIsOpen(!isOpen)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Collapse isOpen={isOpen}>
        <div
          className="sd-model-row__collapse-body"
          data-testid={TEST_IDS.publishProjectDrawer.mapping.collapseBody}
        >
          <ModelMappingForm
            databaseId={databaseId}
            existingModel={existingModel}
            handleChangeCreationType={handleChangeCreationType}
            handleSelectExistingModel={handleSelectExistingModel}
            handleSetModelInfo={handleSetModelInfo}
            modelInfo={modelInfo}
            modelNamePlaceholder={modelNamePlaceholder}
            namedGraphIriPlaceholder={namedGraphIriPlaceholder}
            validateModelName={
              modelInfo.creationType === CreationType.REPLACE
                ? () => ''
                : validateModelName
            }
            validateNamedGraph={validateNamedGraph}
          />
        </div>
      </Collapse>
    </div>
  );
};

type ModelMappingRowsProps = {
  databaseId: string;
  existingByModelId: Record<string, ModelSelectItem | null>;
  isConsolidating: boolean;
  modelsInfo: ModelsInfo;
  handleSetInfoForModel: (
    modelId: string,
    nextInfo: Partial<ModelInfo>
  ) => void;
  handleSelectExistingForModel: (
    modelId: string,
    modelItem: ModelSelectItem | null
  ) => void;
  handleChangeCreationTypeForModel: (
    modelId: string,
    creationType: CreationType
  ) => void;
};

export const ModelMappingRows: React.VFC<ModelMappingRowsProps> = ({
  databaseId,
  existingByModelId,
  handleChangeCreationTypeForModel,
  handleSelectExistingForModel,
  handleSetInfoForModel,
  isConsolidating,
  modelsInfo,
}) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const modelNamePlaceholderByModelId =
    useModelNamePlaceholderByModelId(databaseId);
  const namedGraphIriPlaceholderByModelId =
    useModelNamedGraphPlaceholderByModelId(databaseId);

  const validateModelName = useValidateModelName({
    databaseId,
    targetModelId: project?.targetModelId,
    isConsolidating,
    modelsInfo,
  });
  const validateNamedGraph = useValidateNamedGraphIri(
    databaseId,
    project?.targetModelId,
    isConsolidating,
    modelsInfo
  );

  return (
    <div>
      <div className={classNames('sd-model-row-header', 'sd-model-row-grid')}>
        <span>
          {
            copy.components.publishProjectDrawer.modelConfiguration.columnHeader
              .publish
          }
        </span>
        <span>
          {
            copy.components.publishProjectDrawer.modelConfiguration.columnHeader
              .model
          }
        </span>
      </div>
      {Object.entries(modelsInfo).map(([modelId, modelInfo], idx) => {
        const existingDbModel = existingByModelId[modelId];
        const modelNamePlaceholder = modelNamePlaceholderByModelId[modelId];
        const namedGraphIriPlaceholder =
          namedGraphIriPlaceholderByModelId[modelId];
        const handleChangeCreationType = (creationType: CreationType) => {
          handleChangeCreationTypeForModel(modelId, creationType);
        };
        const handleSelectExistingModel = (
          existing: ModelSelectItem | null
        ) => {
          handleSelectExistingForModel(modelId, existing);
        };
        const handleSetModelInfo = (modelInfo: Partial<ModelInfo>) => {
          handleSetInfoForModel(modelId, modelInfo);
        };

        return (
          <ModelMappingRow
            key={modelInfo.projectModelId}
            databaseId={databaseId}
            existingModel={existingDbModel}
            handleChangeCreationType={handleChangeCreationType}
            handleSelectExistingModel={handleSelectExistingModel}
            handleSetModelInfo={handleSetModelInfo}
            modelInfo={modelInfo}
            modelNamePlaceholder={modelNamePlaceholder}
            namedGraphIriPlaceholder={namedGraphIriPlaceholder}
            testId={getPublishProjectDrawerModelRowTestId(
              modelInfo.projectModelId,
              idx
            )}
            validateModelName={(modelName) =>
              validateModelName({ ...modelInfo, modelName })
            }
            validateNamedGraph={(namedGraphIri) =>
              validateNamedGraph({ ...modelInfo, namedGraphIri })
            }
          />
        );
      })}
    </div>
  );
};
